/**
 * TypeScript implementation of Levenberg-Marquardt algorithm
 * Simplified version focused on root finding for moment constraints
 *
 * This is based on the implementation in ml-levenberg-marquardt (MIT license), translated by Augment,
 * because I couldn't get the commonjs module to load properly in tests.
 *
 * This is tested indirectly via estimateDirectionalSpectrum, but if we want to use it for anything else,
 * we should move it out of this subdirectory and give it a dedicated test suite.
 */

export interface LevenbergMarquardtOptions {
    /** Array of initial parameter values */
    initialValues: number[];
    /** Maximum number of iterations */
    maxIterations?: number;
    /** Error tolerance for convergence */
    errorTolerance?: number;
    /** Finite difference step for gradient calculation */
    gradientDifference?: number;
    /** Initial damping parameter */
    damping?: number;
    /** Factor to decrease damping when improvement is made */
    dampingStepDown?: number;
    /** Factor to increase damping when no improvement */
    dampingStepUp?: number;
    /** Use central differences for gradient calculation */
    centralDifference?: boolean;
}

export interface LevenbergMarquardtResult {
    /** Final parameter values */
    parameterValues: number[];
    /** Final error value */
    parameterError: number;
    /** Number of iterations performed */
    iterations: number;
    /** Whether the algorithm converged */
    converged: boolean;
}

/**
 * Data structure for curve fitting (adapted for root finding)
 */
export interface Data2D {
    x: number[];
    y: number[];
}

/**
 * Function type that takes parameters and returns a function of x
 */
export type ParameterizedFunction = (params: number[]) => (x: number) => number;

/**
 * Simple matrix operations for Levenberg-Marquardt
 */
class SimpleMatrix {
    private data: number[][];
    public rows: number;
    public cols: number;

    constructor(rows: number, cols: number, data?: number[][]) {
        this.rows = rows;
        this.cols = cols;
        if (data) {
            this.data = data;
        } else {
            this.data = Array(rows)
                .fill(null)
                .map(() => Array(cols).fill(0));
        }
    }

    get(row: number, col: number): number {
        return this.data[row]![col]!;
    }

    set(row: number, col: number, value: number): void {
        this.data[row]![col] = value;
    }

    static eye(size: number, damping: number = 1): SimpleMatrix {
        const matrix = new SimpleMatrix(size, size);
        for (let i = 0; i < size; i++) {
            matrix.set(i, i, damping);
        }
        return matrix;
    }

    transpose(): SimpleMatrix {
        const result = new SimpleMatrix(this.cols, this.rows);
        for (let i = 0; i < this.rows; i++) {
            for (let j = 0; j < this.cols; j++) {
                result.set(j, i, this.get(i, j));
            }
        }
        return result;
    }

    multiply(other: SimpleMatrix): SimpleMatrix {
        if (this.cols !== other.rows) {
            throw new Error('Matrix dimensions incompatible for multiplication');
        }
        const result = new SimpleMatrix(this.rows, other.cols);
        for (let i = 0; i < this.rows; i++) {
            for (let j = 0; j < other.cols; j++) {
                let sum = 0;
                for (let k = 0; k < this.cols; k++) {
                    sum += this.get(i, k) * other.get(k, j);
                }
                result.set(i, j, sum);
            }
        }
        return result;
    }

    add(other: SimpleMatrix): SimpleMatrix {
        if (this.rows !== other.rows || this.cols !== other.cols) {
            throw new Error('Matrix dimensions must match for addition');
        }
        const result = new SimpleMatrix(this.rows, this.cols);
        for (let i = 0; i < this.rows; i++) {
            for (let j = 0; j < this.cols; j++) {
                result.set(i, j, this.get(i, j) + other.get(i, j));
            }
        }
        return result;
    }

    scale(factor: number): SimpleMatrix {
        const result = new SimpleMatrix(this.rows, this.cols);
        for (let i = 0; i < this.rows; i++) {
            for (let j = 0; j < this.cols; j++) {
                result.set(i, j, this.get(i, j) * factor);
            }
        }
        return result;
    }

    /**
     * Solve Ax = b using Gaussian elimination
     */
    solve(b: SimpleMatrix): SimpleMatrix {
        if (this.rows !== this.cols) {
            throw new Error('Matrix must be square for solving');
        }
        if (b.rows !== this.rows) {
            throw new Error('Right-hand side dimensions incompatible');
        }

        const n = this.rows;
        const augmented: number[][] = [];

        // Create augmented matrix
        for (let i = 0; i < n; i++) {
            augmented[i] = [];
            for (let j = 0; j < n; j++) {
                augmented[i]![j] = this.get(i, j);
            }
            for (let j = 0; j < b.cols; j++) {
                augmented[i]![n + j] = b.get(i, j);
            }
        }

        // Forward elimination with partial pivoting
        for (let i = 0; i < n; i++) {
            // Find pivot
            let maxRow = i;
            for (let k = i + 1; k < n; k++) {
                if (Math.abs(augmented[k]![i]!) > Math.abs(augmented[maxRow]![i]!)) {
                    maxRow = k;
                }
            }

            // Swap rows
            [augmented[i], augmented[maxRow]] = [augmented[maxRow]!, augmented[i]!];

            // Check for singular matrix
            if (Math.abs(augmented[i]![i]!) < 1e-12) {
                throw new Error('Matrix is singular or nearly singular');
            }

            // Eliminate column
            for (let k = i + 1; k < n; k++) {
                const factor = augmented[k]![i]! / augmented[i]![i]!;
                for (let j = i; j < n + b.cols; j++) {
                    augmented[k]![j]! -= factor * augmented[i]![j]!;
                }
            }
        }

        // Back substitution
        const result = new SimpleMatrix(n, b.cols);
        for (let j = 0; j < b.cols; j++) {
            for (let i = n - 1; i >= 0; i--) {
                let sum = augmented[i]![n + j]!;
                for (let k = i + 1; k < n; k++) {
                    sum -= augmented[i]![k]! * result.get(k, j);
                }
                result.set(i, j, sum / augmented[i]![i]!);
            }
        }

        return result;
    }

    toArray(): number[] {
        const result: number[] = [];
        for (let i = 0; i < this.rows; i++) {
            for (let j = 0; j < this.cols; j++) {
                result.push(this.get(i, j));
            }
        }
        return result;
    }
}

/**
 * Calculate numerical gradient using finite differences
 */
function calculateGradient(
    data: Data2D,
    params: number[],
    parameterizedFunction: ParameterizedFunction,
    gradientDifference: number,
    centralDifference: boolean
): SimpleMatrix {
    const m = data.x.length;
    const n = params.length;
    const jacobian = new SimpleMatrix(m, n);

    const func = parameterizedFunction(params);
    const f0 = data.x.map((x) => func(x));

    for (let j = 0; j < n; j++) {
        const paramsPlus = [...params];
        const paramsMinus = [...params];

        paramsPlus[j]! += gradientDifference;

        if (centralDifference) {
            paramsMinus[j]! -= gradientDifference;
            const funcPlus = parameterizedFunction(paramsPlus);
            const funcMinus = parameterizedFunction(paramsMinus);

            for (let i = 0; i < m; i++) {
                const derivative = (funcPlus(data.x[i]!) - funcMinus(data.x[i]!)) / (2 * gradientDifference);
                jacobian.set(i, j, derivative);
            }
        } else {
            const funcPlus = parameterizedFunction(paramsPlus);

            for (let i = 0; i < m; i++) {
                const derivative = (funcPlus(data.x[i]!) - f0[i]!) / gradientDifference;
                jacobian.set(i, j, derivative);
            }
        }
    }

    return jacobian;
}

/**
 * Calculate residual error
 */
function calculateError(data: Data2D, params: number[], parameterizedFunction: ParameterizedFunction): number {
    const func = parameterizedFunction(params);
    let error = 0;

    for (let i = 0; i < data.x.length; i++) {
        const residual = data.y[i]! - func(data.x[i]!);
        error += residual * residual;
    }

    return Math.sqrt(error / data.x.length);
}

/**
 * Levenberg-Marquardt algorithm implementation
 */
export function levenbergMarquardt(
    data: Data2D,
    parameterizedFunction: ParameterizedFunction,
    options: LevenbergMarquardtOptions
): LevenbergMarquardtResult {
    // Set default options
    const maxIterations = options.maxIterations ?? 100 * (data.x.length + 1);
    const errorTolerance = options.errorTolerance ?? 1e-6;
    const gradientDifference = options.gradientDifference ?? 1e-8;
    const dampingStepDown = options.dampingStepDown ?? 9;
    const dampingStepUp = options.dampingStepUp ?? 11;
    const centralDifference = options.centralDifference ?? false;

    let damping = options.damping ?? 0.01;
    let params = [...options.initialValues];

    let error = calculateError(data, params, parameterizedFunction);
    let bestError = error;
    let bestParams = [...params];

    let iteration = 0;
    let converged = error <= errorTolerance;

    for (; iteration < maxIterations && !converged; iteration++) {
        // Calculate Jacobian matrix
        const jacobian = calculateGradient(data, params, parameterizedFunction, gradientDifference, centralDifference);

        // Calculate residuals
        const func = parameterizedFunction(params);
        const residuals = new SimpleMatrix(data.x.length, 1);
        for (let i = 0; i < data.x.length; i++) {
            residuals.set(i, 0, data.y[i]! - func(data.x[i]!));
        }

        // Calculate JtJ + λI
        const JtJ = jacobian.transpose().multiply(jacobian);
        const identity = SimpleMatrix.eye(params.length, damping);
        const A = JtJ.add(identity);

        // Calculate Jt * residuals
        const Jtr = jacobian.transpose().multiply(residuals);

        try {
            // Solve (JtJ + λI) * delta = Jt * residuals
            const delta = A.solve(Jtr);

            // Update parameters
            const newParams = [...params];
            for (let i = 0; i < params.length; i++) {
                newParams[i]! += delta.get(i, 0);
            }

            // Calculate new error
            const newError = calculateError(data, newParams, parameterizedFunction);

            if (newError < error) {
                // Accept the step
                params = newParams;
                error = newError;
                damping = Math.max(damping / dampingStepDown, 1e-7);

                if (error < bestError) {
                    bestError = error;
                    bestParams = [...params];
                }
            } else {
                // Reject the step, increase damping
                damping = Math.min(damping * dampingStepUp, 1e7);
            }
        } catch {
            // If matrix is singular, increase damping
            damping = Math.min(damping * dampingStepUp, 1e7);
        }

        converged = error <= errorTolerance;
    }

    return {
        parameterValues: bestParams,
        parameterError: bestError,
        iterations: iteration,
        converged: converged,
    };
}
